import { __awaiter, __rest } from "tslib";
import React, { useCallback, useEffect, useState, } from 'react';
import { LocalizationProvider as GBUILocalizationProvider, useLocalization as useGBUILocalization, } from '@groupby/ui-components';
import { isNil } from 'ramda';
import { useAppDispatch } from '@/store/hooks';
import MessageDefinitions from './messages/message-definitions';
import { Localization } from '@/localization';
import { getConfig } from '@/config';
import { addError, addSuccess } from '@/store/slices/notificationsSlice';
import { FeatureFlag, useFeatureFlagsProvider } from '@/search-for-retail/feature-flags';
import { useUpdateUserSettingsMutation } from '@/store/api';
import { useLocale } from './locale.provider';
import { logError } from '@/utils/logger';
export const loadCompiledMessages = (languageCode, featureFlagsService) => __awaiter(void 0, void 0, void 0, function* () {
    var _a;
    try {
        const isUnverifiedTranslationsEnabled = featureFlagsService.isFeatureEnabled(FeatureFlag.UnverifiedTranslations);
        const messageVerificationStatusPath = isUnverifiedTranslationsEnabled && languageCode !== Localization.DEFAULT_LANGUAGE_CODE ? 'unverified/' : '';
        const messages = yield import(`./messages/${messageVerificationStatusPath}compiled/${languageCode}.json`);
        const customer = (_a = getConfig().customer) === null || _a === void 0 ? void 0 : _a.trim().toLowerCase();
        let customerMessages = {};
        try {
            const customerOverrides = yield import(`../customers/${customer}/localization/messages/compiled/${languageCode}.json`);
            customerMessages = customerOverrides.default;
        }
        catch (_b) {
            customerMessages = {};
        }
        const mergedMessages = Object.assign(Object.assign({}, messages.default), customerMessages);
        return mergedMessages;
    }
    catch (err) {
        const defaultMessages = yield import(`./messages/compiled/${Localization.DEFAULT_LANGUAGE_CODE}.json`);
        return defaultMessages.default;
    }
});
export const LocalizationProvider = ({ children }) => {
    const dispatch = useAppDispatch();
    const { locale } = useLocale();
    const { featureFlagsService } = useFeatureFlagsProvider();
    const [updateUserSettings] = useUpdateUserSettingsMutation();
    const [messages, setMessages] = useState(null);
    useEffect(() => {
        const action = () => __awaiter(void 0, void 0, void 0, function* () {
            setMessages(yield loadCompiledMessages(locale, featureFlagsService));
        });
        void action();
    }, [locale, featureFlagsService]);
    const updateUserLanguageSettings = useCallback((locale) => __awaiter(void 0, void 0, void 0, function* () {
        try {
            yield updateUserSettings({ preferredLanguage: locale }).unwrap();
            dispatch(addSuccess({ key: 'TASK_USER_SETTINGS_UPDATE_SUCCESS' }));
        }
        catch (e) {
            const errorMessage = 'TASK_USER_SETTINGS_UPDATE_ERROR';
            logError(errorMessage, e);
            dispatch(addError({ key: errorMessage }));
        }
    }), [dispatch, updateUserSettings]);
    const setLocale = (locale) => updateUserLanguageSettings(locale);
    if (messages === null) {
        return null;
    }
    return (<GBUILocalizationProvider locale={locale} defaultLocale={Localization.DEFAULT_LANGUAGE_CODE} messages={messages} setLocale={setLocale}>
      {children}
    </GBUILocalizationProvider>);
};
export const useLocalization = () => {
    const _a = useGBUILocalization(), { formatMessage: baseFormatMessage } = _a, rest = __rest(_a, ["formatMessage"]);
    function formatMessage(message) {
        const messageDefinition = MessageDefinitions[message.key];
        if (isNil(messageDefinition)) {
            return message.key;
        }
        return baseFormatMessage(messageDefinition, message.values);
    }
    return Object.assign(Object.assign({}, rest), { formatMessage });
};
